import { useRef, useState } from "react";
import { Image as ImageIcon, Upload, X } from "lucide-react";
import { fileUrl, uploadImage } from "../api/files";
import { ApiError } from "../api/client";

interface Props {
  /** Current cover file uuid; null = no cover */
  coverFileId: string | null;
  onChange: (newCoverFileId: string | null) => Promise<void>;
  disabled?: boolean;
}

export function CoverUploader({ coverFileId, onChange, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const result = await uploadImage(file);
      await onChange(result.id);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "FILE_TOO_LARGE") setError("Файл слишком большой (макс. 50 МБ)");
        else if (err.code === "FILE_MIME_NOT_ALLOWED") setError("Тип файла не поддерживается");
        else setError(`Ошибка ${err.status}`);
      } else {
        setError("Сеть недоступна");
      }
    } finally {
      setUploading(false);
      // reset input so picking the same file again triggers change
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function handleClear() {
    if (!confirm("Убрать обложку?")) return;
    try {
      await onChange(null);
    } catch (err) {
      setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  return (
    <div className="space-y-2">
      {coverFileId ? (
        <div className="relative overflow-hidden rounded-xl ring-1 ring-line">
          <img
            src={fileUrl(coverFileId)}
            alt="Обложка курса"
            className="aspect-[16/9] w-full object-cover"
          />
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-2 rounded-full bg-black/50 p-1.5 text-white transition hover:bg-black/70"
              title="Убрать обложку"
            >
              <X size={14} />
            </button>
          )}
        </div>
      ) : (
        <div className="flex aspect-[16/9] w-full items-center justify-center rounded-xl border-2 border-dashed border-line bg-surface-alt text-ink-muted">
          <div className="text-center">
            <ImageIcon size={28} className="mx-auto mb-1 opacity-60" />
            <div className="text-xs">Обложка не задана</div>
          </div>
        </div>
      )}
      {!disabled && (
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
            onChange={handlePick}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="btn-secondary inline-flex items-center gap-2"
          >
            <Upload size={14} />
            {uploading ? "Загружаем…" : coverFileId ? "Заменить" : "Загрузить обложку"}
          </button>
          {error && <span className="text-xs text-red-700">{error}</span>}
        </div>
      )}
    </div>
  );
}
